import supabase from "@/features/supabase";
import { store } from "@/store";
import { SeveritiesView, supabaseApi, useGetActiveSeveritiesQuery } from "@/store/services/supabase";
import { severitiesActions } from "@/store/slices/severities";
import { useKeys } from "@/utils/hooks/useKeys";
import * as Sentry from "@sentry/nextjs";
import { notification } from "antd";
import { useEffect } from "react";
import { postSeveritiesRun, UNEXPECTED_FORMAT_ERROR } from "../utils/post";
import { trackSeveritiesProgress } from "../utils/progress";
import { toSeveritiesJobSchema } from "../utils/supabase";

// Module-level so leaving and reopening the page doesn't post or track the same run twice.
const postingRunIds = new Set<string>();
let inProcessingRuns: { id: string; controller: AbortController }[] = [];

/** Abort runs no longer active, usually because the user deleted them. */
const abortCancelledRuns = (activeRuns: SeveritiesView[]) => {
  inProcessingRuns
    .filter(({ id }) => !activeRuns.some((run) => run.id === id))
    .forEach(({ controller }) => controller.abort());
};

/** Abort requests to /progress if there's no session. */
const abortRunsIfNoSession = async () => {
  const session = (await supabase.auth.getSession()).data.session;
  if (session) return;
  inProcessingRuns.forEach(({ controller }) => controller.abort());
};

/** Notification for failed run before it's deleted. Uses the static `notification` so it shows on any page. */
const notifyPostError = async (err: unknown, severityId: string) => {
  const status = err && typeof err === "object" && "status" in err ? err.status : undefined;
  // A rejected batch throws its row messages; only the first decides whether it's a format error.
  const isFormatError = status === 422 || (Array.isArray(err) && err[0] === UNEXPECTED_FORMAT_ERROR);

  if (status === 403) {
    notification.error({
      message:
        "An error occurred while contacting our results server. There could be an issue with your network configuration. Please contact XDI Support with the time/date of your analysis so we can troubleshoot.",
      duration: false,
    });
    return;
  }

  if (status === 422) {
    const { data: currentUser } = await store.dispatch(
      supabaseApi.endpoints.getCurrentUser.initiate(undefined, { subscribe: false })
    );
    Sentry.captureException("Severities Error: postSeveritiesRun: 422", {
      tags: { section: "Severities Error" },
      extra: { userId: currentUser?.id, orgId: currentUser?.org_id, severityId, error: err },
    });
  }

  notification.error({
    message: isFormatError
      ? "Uploaded data was in unexpected format. Please contact XDI Support with the time/date of your analysis so we can troubleshoot."
      : "An error occurred while processing your Severities run. Please try again or contact XDI Support.",
    duration: false,
  });
};

/**
 * Posts the current user's unprocessed runs to the Severities API, then follows their progress to completion,
 * keeping live counts in the `severities` slice. A run that fails to post is deleted.
 */
export const useSeveritiesJobs = () => {
  const [keyPair] = useKeys();
  const { data: activeRuns } = useGetActiveSeveritiesQuery(undefined, { skip: !keyPair.user });

  useEffect(() => {
    const user = keyPair.user;
    if (!activeRuns || !user) return;

    abortCancelledRuns(activeRuns);
    void abortRunsIfNoSession();

    activeRuns
      .filter((run) => run.unprocessed > 0 && !postingRunIds.has(run.id!))
      .forEach((run) => {
        const runId = run.id!;
        postingRunIds.add(runId);

        toSeveritiesJobSchema(run, user, keyPair.org)
          .then((schema) =>
            // Deleted on failure, so the run doesn't stay at uploading.
            postSeveritiesRun(schema).catch(async (err) => {
              console.error(`Failed to post Severities run ${runId}:`, err);
              await store
                .dispatch(supabaseApi.endpoints.deleteSeverities.initiate(runId))
                .unwrap()
                // Report the post failure rather than a delete failure that masks it.
                .catch((e) => console.error(`Failed to delete Severities run ${runId}:`, e));
              await notifyPostError(err, runId);
            })
          )
          .catch((err) => console.error(`Failed to read Severities run ${runId}:`, err))
          .finally(() => postingRunIds.delete(runId));
      });

    // Posting finishes by refreshing the active runs, which brings a fully posted run here.
    activeRuns
      // `pending` is only seeded once every row is uploaded, so a run still uploading isn't tracked.
      .filter((run) => run.unprocessed === 0 && run.pending > 0 && !inProcessingRuns.some(({ id }) => id === run.id))
      .forEach((run) => {
        const activeRunId = run.id!;
        const controller = new AbortController();
        inProcessingRuns.push({ id: activeRunId, controller });

        toSeveritiesJobSchema(run, user, keyPair.org)
          .then((schema) =>
            trackSeveritiesProgress(schema, controller.signal, (assets) => {
              // Checked on every line, as the active runs stop refreshing once signed out.
              void abortRunsIfNoSession();
              store.dispatch(severitiesActions.setRunProgress({ id: activeRunId, assets }));
            })
          )
          .catch((err) => {
            if (!controller.signal.aborted) console.error(`Failed to track Severities run ${activeRunId}:`, err);
          })
          .finally(() => {
            inProcessingRuns = inProcessingRuns.filter((processing) => processing.controller !== controller);
          });
      });
  }, [activeRuns, keyPair.user, keyPair.org]);
};
